import { defineStore } from 'pinia';
import { ref, computed, watch } from 'vue';
import autoApplyQueueApi from '../api/autoapply-queue';
import { trackQueueApplicationEvent, trackQueueEvent } from '../composables/autoapply-queue-analytics.js';
import { setAuthState } from '../composables/auth.js';
import { markFirstAutoApplySubmitted } from '../composables/first-autoapply-submitted.js';
import { isMobileViewport } from '../composables/isMobile.js';
import { AUTOAPPLY_QUEUE_EVENTS } from '../constants/analytics-events.js';
import Sentry from '../errors/sentry';
import { PREPARATION_STEP_ORDER, sortForQueue } from '../utils/autoapply-queue.js';
// auto-apply.store.js imports useAutoApplyQueueStore back, but each store calls the other only inside its
// actions (never at setup or module-eval time), so the cycle is inert at runtime.
// eslint-disable-next-line import/no-cycle
import { useAutoApplyJobsStore } from './auto-apply.store.js';
// autoapply-queue-new-user-tutorial.store.js imports this store and the AutoApply one back. Its watchers read this
// store as it sets up, but this store calls it only inside actions (never at setup or module-eval time), so the cycle
// is inert at runtime.
// eslint-disable-next-line import/no-cycle
import { useAutoApplyQueueNewUserTutorialStore } from './autoapply-queue-new-user-tutorial.store.js';
import { useAutoApplyQueueTutorialStore } from './autoapply-queue-tutorial.store.js';
import { useFeatureUsageStore } from './feature-usage.store';
import { useJobBoardStore } from './job-board.store.js';
import { useJobQualitySurveyStore } from './job-quality-survey.store.js';
import { useNotifications } from './notifications.store';
import { useOnboardingRetentionStore } from './onboarding-retention.store';

// How long a job card takes to collapse out of the list, and how long the AutoApply tab glows
// after a job is added (EN-1236).
export const COLLAPSE_MS = 280;
export const GLOW_MS = 1400;
// How long the mobile job sheet takes to fade out: the leave duration in components/sidetab.vue (EN-1253).
export const SHEET_CLOSE_MS = 200;

const ADD_FAILED_MESSAGE = 'We couldn\'t add this job to your AutoApply queue. Please try again.';
const REMOVED_MESSAGE = 'Removed from your AutoApply queue';
const REMOVE_FAILED_MESSAGE = 'We couldn\'t remove this application. Please try again.';

// The toasts of the Complete Auto Apply switch (EN-1249).
const COMPLETE_AUTO_APPLY_ON = 'Complete Auto Apply is on. Ready applications are sent without review, unless one needs '
	+ 'your input';
const COMPLETE_AUTO_APPLY_OFF = 'Complete Auto Apply is off. Every application waits for your review';
const COMPLETE_AUTO_APPLY_FAILED = 'We couldn’t save Complete Auto Apply. Please try again.';

const CHANGE_RESUME_FAILED_MESSAGE = 'We couldn’t change the resume. Please try again.';

// Submit and Cancel Send (EN-1245). The failures have no design, so their copy is a placeholder.
const FIX_FIELDS_MESSAGE = 'Please fix the highlighted fields and try again.';
const SUBMIT_FAILED_MESSAGE = 'We couldn’t submit this application. Please try again.';
const SEND_CANCELLED_MESSAGE = 'Send cancelled. The application is back in review';
const CANCEL_SEND_FAILED_MESSAGE = 'We couldn’t cancel this send. Please try again.';

// One "Application sent" toast, whose count grows and whose time restarts with every send while it is up (EN-1245).
const SENT_TOAST_KEY = 'autoapply-queue-sent';
const SENT_TOAST_MS = 5000;
const SENT_TOAST_LINK = { text: 'JobTrackerAI', to: '/job-tracker' };

const sentToastMessage = (count) => (
	count === 1 ? 'Application sent. Track its status in' : `${count} applications sent. Track their status in`
);

// Placeholder copy (D6): no design covers the resume-mismatch review.
const APPLY_UNTAILORED_FAILED_MESSAGE = 'We couldn’t continue with this application. Please try again.';

// The Couldn't apply actions (EN-1248).
const MARKED_APPLIED_MESSAGE = 'Marked as applied and added to JobTrackerAI';
const MARK_APPLIED_FAILED_MESSAGE = 'We couldn’t mark this application as applied. Please try again.';
const RESUBMIT_FAILED_MESSAGE = 'We couldn’t reopen this application. Please try again.';
const REPORT_SENT_MESSAGE = 'Thanks, your report was sent';
const REPORT_FAILED_MESSAGE = 'We couldn’t send your report. Please try again.';

const SENTRY_CONTEXT = { tags: { area: 'job-board', feature: 'autoapply-queue' } };

// Pusher's event for a channel that has (re)subscribed, after a cold start or a reconnect.
const SUBSCRIPTION_SUCCEEDED = 'pusher:subscription_succeeded';

const emptyCounts = () => ({ all: 0, tab: 0, to_review: 0, by_status: {} });

// jsdom has no matchMedia.
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

const wait = (ms) => new Promise((resolve) => {
	setTimeout(resolve, ms);
});

export const useAutoApplyQueueStore = defineStore('autoapply-queue', () => {
	const { getUser } = setAuthState();

	let queueChannel = null;
	let subscriberCount = 0;
	let glowTimer = null;
	let glowResolvers = [];
	// The fetch in flight and the events that arrived while it ran. clearQueue() and the end of a fetch each
	// replace or release it, so a fetch that was cleared away can tell it no longer owns the queue.
	let queueLoad = null;
	// Bumped by every Complete Auto Apply save, so a queue load that started before one cannot undo it.
	let completeAutoApplySaves = 0;
	// The applications already announced as sent, and how many the toast on screen counts.
	const announcedSentUuids = new Set();
	let sentToastCount = 0;

	// The server owns these numbers; the client only replaces them (EN-1236 D3).
	const counts = ref(emptyCounts());
	// Every application in the queue, always in the "All" order. Empty until fetchQueue() has succeeded.
	const applications = ref([]);
	const isQueueLoaded = ref(false);
	const isTabGlowing = ref(false);
	// The job being added, the job whose card is collapsing away, and the job added last: the job board
	// reads them to show AutoApply as busy, animate the card and scroll the details back up to the queued job's
	// button (EN-1303).
	const addingJobId = ref(null);
	const leavingJobId = ref(null);
	// The user's Complete Auto Apply setting, loaded with the queue (EN-1249).
	const completeAutoApply = ref(false);
	const isSavingCompleteAutoApply = ref(false);
	const lastAdded = ref(null);

	// The loaded queue by job board job id, so a page of cards does not scan the list once per card (EN-1303).
	const applicationsByJobId = computed(() => new Map(applications.value.map((row) => [row.job.id, row])));

	const tabCount = computed(() => counts.value.tab);
	const hasNeedsInput = computed(() => (counts.value.by_status?.needs_input ?? 0) > 0);

	const fetchCounts = async () => {
		counts.value = await autoApplyQueueApi.getCounts();
	};

	/**
	 * The queue application of a job, wherever the job is listed (EN-1303). Once the queue is loaded the list is
	 * authoritative: a job it no longer has is not queued, whatever the job's payload says. Before that, the status and
	 * uuid the API put on the job stand in for it, as nothing else knows.
	 *
	 * @param {{ provider_job_id: number, auto_apply_queue_uuid?: string|null, auto_apply_queue_status?: string|null }|null} job
	 * @returns {object|null} The queue row; or, before the load, `{ uuid, status }`; or null when the job is not queued.
	 */
	const applicationForJob = (job) => {
		if (isQueueLoaded.value) {
			return applicationsByJobId.value.get(job?.provider_job_id) ?? null;
		}

		return job?.auto_apply_queue_status
			? { uuid: job.auto_apply_queue_uuid ?? null, status: job.auto_apply_queue_status }
			: null;
	};

	const stepRank = ({ preparation }) => PREPARATION_STEP_ORDER.indexOf(preparation?.step);

	// An update older than the row it would replace is a late delivery. Preparation steps do not move `updated_at`,
	// so between two preparing rows that tie on it the earlier step is the older one.
	const isStale = (incoming, current) => {
		const age = Date.parse(incoming.updated_at) - Date.parse(current.updated_at);

		if (age !== 0) {
			return age < 0;
		}

		return incoming.status === 'preparing' && current.status === 'preparing' && stepRank(incoming) < stepRank(current);
	};

	// A removed application arrives with a null status. A late delivery is dropped.
	const upsertApplication = (incoming) => {
		if (incoming.status === null) {
			applications.value = applications.value.filter(({ uuid }) => uuid !== incoming.uuid);

			return;
		}

		const index = applications.value.findIndex(({ uuid }) => uuid === incoming.uuid);

		if (index !== -1 && isStale(incoming, applications.value[index])) {
			return;
		}

		// Replaced where it stands, as the sort is stable: rows that tie must not swap on every preparation step.
		const next = [...applications.value];

		if (index === -1) {
			next.push(incoming);
		} else {
			next[index] = incoming;
		}

		applications.value = sortForQueue(next);
	};

	const applyQueueCounts = (event) => {
		if (event.queue?.counts) {
			counts.value = event.queue.counts;
		}
	};

	const applyQueueRow = (event) => {
		if (event.queue?.application) {
			upsertApplication(event.queue.application);
		}
	};

	const applyQueueEvent = (event) => {
		applyQueueCounts(event);
		applyQueueRow(event);
	};

	/**
	 * Hands something to a queue tutorial: EN-1250's tour or the new user tutorial (EN-1251). A tutorial only guides:
	 * if it throws (its store is created on the first call), the throw is reported and goes no further, or it would
	 * lose a queue row or turn an add or a submit the server accepted into a failed one.
	 *
	 * @param {() => *} handOff
	 * @returns {*} What the hand-off returned, or undefined when it threw.
	 */
	const tellTutorial = (handOff) => {
		try {
			return handOff();
		} catch (error) {
			Sentry.captureException(error, SENTRY_CONTEXT);

			return undefined;
		}
	};

	// Broadcasts from before the queue carry no `queue` key; they leave the counts and the list alone.
	const onQueueEvent = (event) => {
		// First, whatever the load state: the new user tutorial follows its application while the queue list is
		// unloaded or still loading, and the rows are held back then.
		if (event.queue?.application) {
			tellTutorial(() => useAutoApplyQueueNewUserTutorialStore().onApplicationChanged(event.queue.application));
		}

		if (queueLoad) {
			// The tab counter must not lag behind a slow fetch, but the row waits: the response may predate the
			// event, and the response would overwrite the row if it were patched now.
			applyQueueCounts(event);
			queueLoad.events.push(event);
		} else if (isQueueLoaded.value) {
			applyQueueEvent(event);
		} else {
			applyQueueCounts(event);
		}
	};

	/**
	 * "Application sent" for an application the submitter has just sent (EN-1245). Only the job board's tabs subscribe
	 * to the queue, so only a seeker on JobBoardAI sees it. Mark as Applied reaches Sent too, but nothing was sent. The
	 * submitter can broadcast a send twice, so each application is announced once.
	 */
	const announceSent = (application) => {
		if (application?.status !== 'sent' || application.submitted_via === 'manual'
			|| announcedSentUuids.has(application.uuid)) {
			return;
		}

		announcedSentUuids.add(application.uuid);

		const notifications = useNotifications();
		sentToastCount = notifications.isShowing(SENT_TOAST_KEY) ? sentToastCount + 1 : 1;

		notifications.notify(sentToastMessage(sentToastCount), 'success', SENT_TOAST_MS, null, null, null, {
			key: SENT_TOAST_KEY,
			link: SENT_TOAST_LINK,
		});
	};

	// Only a broadcast announces a send: onQueueEvent also takes this tab's own responses.
	const onQueueBroadcast = (event) => {
		announceSent(event.queue?.application);
		onQueueEvent(event);
	};

	const runQueueLoad = async (load) => {
		try {
			const response = await autoApplyQueueApi.getQueue();

			// clearQueue() ran meanwhile: this response belongs to a queue nobody is showing.
			if (queueLoad !== load) {
				return;
			}

			applications.value = sortForQueue(response.applications);
			counts.value = response.counts;
			isQueueLoaded.value = true;

			// A save in flight, or one made since this load started, wins over a response that may predate it.
			if (!isSavingCompleteAutoApply.value && load.completeAutoApplySaves === completeAutoApplySaves) {
				completeAutoApply.value = response.completeAutoApply;
			}

			// Only the rows: the events' counts were applied as they arrived, and the broadcast is queued, so an
			// event that arrived during the fetch usually predates the response. Replaying its counts would roll
			// the counter back; a row that does predate the response is dropped as stale.
			load.events.forEach(applyQueueRow);
		} catch (error) {
			// A failed reload keeps the list, but the rows that changed meanwhile still belong in it. With no list
			// to patch, there is nothing to replay onto.
			if (queueLoad === load && isQueueLoaded.value) {
				load.events.forEach(applyQueueRow);
			}

			// Reported here, once, as several callers may share this load. sentryBeforeSend drops the axios ones, so
			// what reaches Sentry is a bug such as a malformed payload.
			Sentry.captureException(error, SENTRY_CONTEXT);

			throw error;
		}
	};

	/**
	 * Load the whole queue. A call made while one is in flight shares it. A failure is reported once and rejects, so
	 * the caller decides whether to show it: a failed first load leaves the queue unloaded, and a failed reload keeps
	 * the list but still applies the rows of the events that arrived while it ran. A load interrupted by clearQueue()
	 * resolves without loading anything.
	 */
	const fetchQueue = () => {
		if (!queueLoad) {
			const load = { events: [], promise: null, completeAutoApplySaves };

			queueLoad = load;
			load.promise = runQueueLoad(load).finally(() => {
				if (queueLoad === load) {
					queueLoad = null;
				}
			});
		}

		return queueLoad.promise;
	};

	/**
	 * Forget the list, and any fetch still in flight: its response and the events buffered for it are dropped, and
	 * the next fetchQueue() starts a new request.
	 */
	const clearQueue = () => {
		queueLoad = null;
		applications.value = [];
		isQueueLoaded.value = false;
	};

	/**
	 * Catch up on what Pusher dropped: events broadcast before the channel finished subscribing (on a cold load the
	 * queue request usually answers first) or while it reconnected. A loaded queue, or one loading, is fetched again
	 * once any load in flight has settled, as that load may have been answered before the missed event. The queue
	 * response carries the counts, so only a queue nobody shows refetches them on their own.
	 */
	const onSubscribed = async () => {
		const refetchCounts = () => fetchCounts().catch((error) => Sentry.captureException(error, SENTRY_CONTEXT));

		if (!queueLoad && !isQueueLoaded.value) {
			refetchCounts();

			return;
		}

		await queueLoad?.promise.catch(() => {});

		// The list is gone: clearQueue() ran meanwhile, or the first load failed and the page shows that.
		if (!queueLoad && !isQueueLoaded.value) {
			refetchCounts();

			return;
		}

		fetchQueue().catch(() => {});
	};

	/**
	 * Follow the queue's counts live. The AutoApply tab and the queue page both subscribe, so the
	 * channel is bound for the first subscriber only and released after the last one leaves. Every
	 * call refetches, since events missed while nobody listened are not replayed.
	 */
	const subscribe = () => {
		subscriberCount += 1;

		const currentUser = getUser();

		if (!queueChannel && currentUser) {
			queueChannel = window.Echo.private(`auto-apply.${currentUser.id}`);
			queueChannel.listen('AutoApplyQueueEvent', onQueueBroadcast);
			// Not Echo's subscribed(): it wraps the callback, which then cannot be unbound.
			queueChannel.on(SUBSCRIPTION_SUCCEEDED, onSubscribed);
		}

		return fetchCounts();
	};

	const unsubscribe = () => {
		if (subscriberCount === 0) { return; }

		subscriberCount -= 1;

		if (subscriberCount === 0 && queueChannel) {
			queueChannel.stopListening('AutoApplyQueueEvent', onQueueBroadcast);
			// Only this handler: other stores share the auto-apply channel object.
			queueChannel.subscription.unbind(SUBSCRIPTION_SUCCEEDED, onSubscribed);
			queueChannel = null;
		}
	};

	/**
	 * Highlight the AutoApply tab for GLOW_MS. Glowing again restarts the window, and every caller
	 * still waiting resolves when that last window ends.
	 */
	const glowTab = () => new Promise((resolve) => {
		clearTimeout(glowTimer);
		glowResolvers.push(resolve);
		isTabGlowing.value = true;

		glowTimer = setTimeout(() => {
			isTabGlowing.value = false;
			glowResolvers.forEach((resolveGlow) => resolveGlow());
			glowResolvers = [];
		}, GLOW_MS);
	});

	const notifyAddFailed = (message) => {
		useNotifications().notify(message || ADD_FAILED_MESSAGE, 'error');

		return 'failed';
	};

	/**
	 * On mobile the tap came from the full-screen job sheet: close it, or it would silently switch to the
	 * neighbour with the collapse and the glow hidden underneath. Desktop has no sheet, though it sets the flag
	 * too. Resolves once the sheet has faded out, so the seeker sees their card go. A sheet the seeker has since
	 * opened for another job stays open.
	 */
	const closeJobSheet = async (jobBoardStore, job) => {
		if (jobBoardStore.selectedJob?.provider_job_id !== job.provider_job_id) {
			return;
		}

		const isSheetOpen = isMobileViewport() && jobBoardStore.showMobileJobDetails;
		jobBoardStore.closeMobileJobDetails();

		if (isSheetOpen && !prefersReducedMotion()) {
			await wait(SHEET_CLOSE_MS);
		}
	};

	const sendToQueue = async (job, resume) => {
		// An AutoApply click moves the user out of the job quality survey's "engaged but didn't act" segment.
		useJobQualitySurveyStore().resetCounters();

		// Read before the add, which itself makes it true, for the queue tour (EN-1250).
		const hadUsedAutoApply = getUser()?.has_used_auto_apply === true;

		const jobBoardStore = useJobBoardStore();
		const response = await autoApplyQueueApi.addToQueue({
			jobBoardJobId: job.provider_job_id,
			resumeUuid: resume.uuid,
			queryId: job.criteriaQueryId ?? null,
			source: jobBoardStore.autoApplySourceFor(job),
		});

		// Only the out-of-credits 429 carries has_premium_access; a throttle 429 is a plain failure.
		if (response.status === 429 && typeof response.data?.has_premium_access === 'boolean') {
			useAutoApplyJobsStore().showOutOfCreditsPaywall(job, response.data);

			return 'no_credits';
		}

		if (response.status >= 400) {
			return notifyAddFailed(response.data?.message);
		}

		counts.value = response.data.meta.counts;
		// A loaded queue gets the row now, as the Saved tab and the details pane read the job's status from it
		// (EN-1303). The queue's events can arrive after the response, or not at all on a page that has not subscribed.
		onQueueEvent({ queue: { application: response.data.data } });
		useFeatureUsageStore().refresh();

		const glowing = glowTab();

		// A single job opened by id stays on screen, so its card never collapses (D6). With reduced motion
		// the card leaves at once (D5).
		if (!jobBoardStore.isSharedLinkFound) {
			await closeJobSheet(jobBoardStore, job);
			leavingJobId.value = job.provider_job_id;

			if (!prefersReducedMotion()) {
				await wait(COLLAPSE_MS);
			}
		}

		const hasSelectionChanged = jobBoardStore.removeQueuedJob(job);
		leavingJobId.value = null;

		// Set once the neighbour is selected, so the details pane scrolls to the top of the new job. A job the
		// seeker opened meanwhile keeps its scroll position.
		if (hasSelectionChanged) {
			lastAdded.value = { jobId: job.provider_job_id, at: Date.now() };
		}

		// The queue tour opens once the card has gone, while the tab still glows (EN-1250, Figma 02b).
		if (response.status === 201) {
			const application = response.data.data;
			// Step 2 of the new user tutorial opens while the tab still glows, and addingJobId is still set (EN-1251).
			const isNewUserTutorialAdd = tellTutorial(
				() => useAutoApplyQueueNewUserTutorialStore().onAdded(application),
			) === true;
			// An add the new user tutorial took is a new user's, even when a user cached since its step 1 says otherwise:
			// the queue tour records its flag and never opens, so the two tours never run on one add.
			tellTutorial(() => useAutoApplyQueueTutorialStore().startAfterAdd(
				application,
				hadUsedAutoApply && !isNewUserTutorialAdd,
			));
		}

		await glowing;

		return response.status === 201 ? 'added' : 'already_queued';
	};

	/**
	 * The AutoApply click for a queue user (EN-1236): add the job to the queue, collapse its card out of the
	 * list, open its neighbour and glow the tab. Clicks are ignored from this one until the glow ends (D4), as
	 * the neighbour's AutoApply button lands under the cursor.
	 *
	 * @returns {Promise<'added'|'already_queued'|'no_credits'|'failed'|'ignored'>}
	 */
	const addToQueue = async (job, resume) => {
		if (addingJobId.value !== null) {
			return 'ignored';
		}

		addingJobId.value = job.provider_job_id;

		try {
			return await sendToQueue(job, resume);
		} catch {
			// A network error, or anything after the response that throws (a 2xx without `meta`). Nothing has
			// been shown yet, and the click lock must not stay on.
			leavingJobId.value = null;

			return notifyAddFailed();
		} finally {
			addingJobId.value = null;
		}
	};

	// With Complete Auto Apply on, the first application goes out without a Submit, so the first-application paywall
	// waits for a sent one instead (EN-1245): the moment one is sent while the seeker is on JobBoardAI, or their next
	// visit there, as the tab bar loads the counts. The retention store keeps today's audience and shows it once.
	watch(() => (counts.value.by_status?.sent ?? 0) > 0, (hasSent) => {
		if (hasSent) {
			useOnboardingRetentionStore().recordFirstApplicationSubmitted();
		}
	});

	/**
	 * Submit Application (EN-1245): approve the answers the review shows and send the application on the next
	 * dispatcher run. An edit still waiting on its debounce is saved first. The returned row goes the way a broadcast
	 * row does, so the review moves on without waiting for Pusher. Nothing is charged, so the credits stay as they are.
	 *
	 * A refusal is shown, never thrown: answers the server rejects are marked on the form, and an application that can
	 * no longer be submitted (no longer waiting for review, its job gone) shows the server's reason.
	 *
	 * @returns {Promise<boolean>} Whether the application was submitted.
	 */
	const submitApplication = async (uuid) => {
		const autoApplyStore = useAutoApplyJobsStore();
		// The row at the click: the event reports the status it had then, and the response replaces the row.
		const application = applications.value.find((row) => row.uuid === uuid);

		// At the click, as the job board's review form does: the seeker has answered by now, and a broadcast that beats
		// the response opens the next application, which must read the flag already.
		markFirstAutoApplySubmitted();

		try {
			await autoApplyStore.flushPendingUpdates();
		} catch (error) {
			useNotifications().notify(error.message || SUBMIT_FAILED_MESSAGE, 'error');

			return false;
		}

		try {
			onQueueEvent({ queue: await autoApplyQueueApi.submit(uuid) });
		} catch (error) {
			const { submission: refusal, ...fieldErrors } = error.response?.status === 422
				? error.response.data?.errors ?? {}
				: {};

			if (Object.keys(fieldErrors).length) {
				autoApplyStore.validationErrors = fieldErrors;
				useNotifications().notify(FIX_FIELDS_MESSAGE, 'error');
			} else {
				useNotifications().notify(refusal?.[0] || SUBMIT_FAILED_MESSAGE, 'error');
			}

			// A refusal means the row the user acted on is out of date, e.g. its job has gone and the broadcast saying so
			// has not arrived (EN-1299). Reloading shows it as it is, rather than leaving Submit to be refused again.
			if (refusal) {
				fetchQueue().catch(() => {});
			}

			return false;
		}

		trackQueueApplicationEvent(AUTOAPPLY_QUEUE_EVENTS.SUBMITTED, application);

		// As the job board's review form does: a first application may surface the paywall.
		useOnboardingRetentionStore().recordFirstApplicationSubmitted();

		return true;
	};

	/**
	 * Cancel Send (EN-1245): a pending application goes back to review. The returned row goes the way a broadcast row
	 * does. Once a worker has claimed the application the server refuses with a 422 and its reason, which is shown, and
	 * the queue reloads: the broadcast saying the row is being sent may not have arrived yet (EN-1321).
	 * Nothing is charged or refunded.
	 *
	 * @returns {Promise<boolean>} Whether the send was cancelled. A failure is shown, never thrown.
	 */
	const cancelSend = async (application) => {
		try {
			onQueueEvent({ queue: await autoApplyQueueApi.cancelSend(application.uuid) });
		} catch (error) {
			const refusal = error.response?.data?.errors?.submission?.[0];

			useNotifications().notify(refusal || CANCEL_SEND_FAILED_MESSAGE, 'error');

			if (refusal) {
				fetchQueue().catch(() => {});
			}

			return false;
		}

		trackQueueApplicationEvent(AUTOAPPLY_QUEUE_EVENTS.SEND_CANCELLED, application);
		useNotifications().notify(SEND_CANCELLED_MESSAGE);

		return true;
	};

	/**
	 * Re-tailor an application to another resume. The returned row goes the way a broadcast row does, so the review
	 * screen can move on without waiting for Pusher, and a reload in flight does not overwrite it. The server refuses
	 * an application that is not waiting for review with a 422 and its reason, which is shown as it comes.
	 *
	 * @returns {Promise<boolean>} Whether the resume was changed. A failure is shown, never thrown.
	 */
	const changeResume = async (uuid, resume) => {
		try {
			const application = await autoApplyQueueApi.changeResume(uuid, resume.uuid);

			onQueueEvent({ queue: { application } });

			return true;
		} catch (error) {
			const refusal = error.response?.data?.errors?.submission?.[0];

			useNotifications().notify(refusal || CHANGE_RESUME_FAILED_MESSAGE, 'error');

			return false;
		}
	};

	/**
	 * "Apply anyway": go on with the untailored resume after tailoring found no overlap. The returned row goes the way
	 * changeResume's does. The server refuses an application that is not waiting on that decision with a 422 and its
	 * reason, which is shown as it comes.
	 *
	 * @returns {Promise<boolean>} Whether the application went on. A failure is shown, never thrown.
	 */
	const applyUntailored = async (uuid) => {
		try {
			const application = await autoApplyQueueApi.applyUntailored(uuid);

			onQueueEvent({ queue: { application } });

			return true;
		} catch (error) {
			const refusal = error.response?.data?.errors?.submission?.[0];

			useNotifications().notify(refusal || APPLY_UNTAILORED_FAILED_MESSAGE, 'error');

			return false;
		}
	};

	/**
	 * Take an application out of the queue (EN-1238). The response goes through onQueueEvent, as the broadcast does,
	 * so a queue load in flight buffers the removal and replays it. Removing can refund the credit, hence the refresh.
	 * A refused removal (422) shows the server's reason; any other failure shows a generic message.
	 *
	 * @returns {Promise<boolean>} Whether the application was removed.
	 */
	const removeFromQueue = async (application) => {
		try {
			onQueueEvent({ queue: await autoApplyQueueApi.removeFromQueue(application.uuid) });
		} catch (error) {
			const reason = error?.response?.status === 422 ? error.response.data?.message : null;

			useNotifications().notify(reason || REMOVE_FAILED_MESSAGE, 'error');

			return false;
		}

		trackQueueApplicationEvent(AUTOAPPLY_QUEUE_EVENTS.REMOVED, application);
		useFeatureUsageStore().refresh();
		useNotifications().notify(REMOVED_MESSAGE);

		return true;
	};

	/**
	 * "Mark as Applied" on a Couldn't apply application (EN-1248): the server moves it to Sent and records it in
	 * JobTrackerAI. The response goes the way removeFromQueue's does. A refused one (422) shows the server's reason.
	 *
	 * @returns {Promise<boolean>} Whether the application was marked as applied. A failure is shown, never thrown.
	 */
	const markApplied = async (application) => {
		try {
			onQueueEvent({ queue: await autoApplyQueueApi.markApplied(application.uuid) });
		} catch (error) {
			const refusal = error.response?.data?.errors?.submission?.[0];

			useNotifications().notify(refusal || MARK_APPLIED_FAILED_MESSAGE, 'error');

			return false;
		}

		trackQueueApplicationEvent(AUTOAPPLY_QUEUE_EVENTS.MARKED_APPLIED, application);
		useNotifications().notify(MARKED_APPLIED_MESSAGE);

		return true;
	};

	/**
	 * "Review & Resubmit" on a validation failure (EN-1248): the server reopens it as Needs your input, free, so the
	 * review can open it straight away. The response goes the way removeFromQueue's does.
	 *
	 * @returns {Promise<boolean>} Whether the application was reopened. A failure is shown, never thrown.
	 */
	const resubmit = async (application) => {
		try {
			onQueueEvent({ queue: await autoApplyQueueApi.resubmit(application.uuid) });

			return true;
		} catch (error) {
			const refusal = error.response?.data?.errors?.submission?.[0];

			useNotifications().notify(refusal || RESUBMIT_FAILED_MESSAGE, 'error');

			return false;
		}
	};

	/**
	 * "Report Issue" (EN-1248): the user's text about one application, with the layout it was sent from. The
	 * application stays as it is.
	 *
	 * @returns {Promise<boolean>} Whether the report was sent. A failure is shown, never thrown.
	 */
	const reportIssue = async (application, message) => {
		try {
			await autoApplyQueueApi.reportIssue(application.uuid, {
				message,
				platform: isMobileViewport() ? 'mobile' : 'desktop',
			});
		} catch (error) {
			const refusal = error.response?.status === 422 ? error.response.data?.message : null;

			useNotifications().notify(refusal || REPORT_FAILED_MESSAGE, 'error');

			return false;
		}

		trackQueueApplicationEvent(AUTOAPPLY_QUEUE_EVENTS.ISSUE_REPORTED, application);
		useNotifications().notify(REPORT_SENT_MESSAGE);

		return true;
	};

	/**
	 * The Complete Auto Apply switch (EN-1249): flips at once, saves, and confirms with a toast. A failed save
	 * flips it back. A flip while a save is in flight is ignored.
	 */
	const setCompleteAutoApply = async (enabled) => {
		if (isSavingCompleteAutoApply.value) {
			return;
		}

		const previous = completeAutoApply.value;
		completeAutoApply.value = enabled;
		isSavingCompleteAutoApply.value = true;
		completeAutoApplySaves += 1;

		try {
			completeAutoApply.value = await autoApplyQueueApi.setCompleteAutoApply(enabled);
			trackQueueEvent(AUTOAPPLY_QUEUE_EVENTS.COMPLETE_AUTO_APPLY_TOGGLED, { enabled: completeAutoApply.value });
			useNotifications().notify(completeAutoApply.value ? COMPLETE_AUTO_APPLY_ON : COMPLETE_AUTO_APPLY_OFF, 'success');
		} catch (error) {
			completeAutoApply.value = previous;
			useNotifications().notify(COMPLETE_AUTO_APPLY_FAILED, 'error');
			Sentry.captureException(error, SENTRY_CONTEXT);
		} finally {
			isSavingCompleteAutoApply.value = false;
		}
	};

	return {
		counts,
		applications,
		isQueueLoaded,
		isTabGlowing,
		addingJobId,
		leavingJobId,
		lastAdded,
		completeAutoApply,
		isSavingCompleteAutoApply,

		tabCount,
		hasNeedsInput,

		applicationForJob,
		fetchCounts,
		fetchQueue,
		clearQueue,
		subscribe,
		unsubscribe,
		glowTab,
		addToQueue,
		submitApplication,
		cancelSend,
		changeResume,
		applyUntailored,
		removeFromQueue,
		markApplied,
		resubmit,
		reportIssue,
		setCompleteAutoApply,
	};
});
